import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, message } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useClosingPackageCommands } from './useClosingPackageCommands';
import { useClosingPackageTaskModalData } from './useClosingPackageTaskModalData';
import {
  buildDefaultScanDispatchRecipient,
  buildScanDispatchBody,
  validateSendScansSubmit,
} from '../lib/sendScansModalValidation';
import { mapClosingPackageApiError } from '../lib/errorMapping';
import {
  buildScanArchiveDownloadFilename,
  collectScanArchiveDocumentEntries,
} from '../lib/sendScansArchive';
import { downloadScanDocumentsArchive } from '../lib/sendScansArchiveDownload';

export type SendScansModalFormValues = {
  dispatchedAt: Dayjs;
  recipient?: string;
  comment?: string;
};

export type UseSendScansModalOptions = {
  applicationId: number;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
};

export function useSendScansModal({
  applicationId,
  open,
  onClose,
  onSuccess,
}: UseSendScansModalOptions) {
  const [form] = Form.useForm<SendScansModalFormValues>();
  const [archiveDownloaded, setArchiveDownloaded] = useState(false);
  const [archiveDownloading, setArchiveDownloading] = useState(false);

  const { application, projection, contextView, contextLoading } = useClosingPackageTaskModalData({
    applicationId,
    open,
  });

  const { registerScanDispatch, states } = useClosingPackageCommands();

  const defaultRecipient = useMemo(
    () => buildDefaultScanDispatchRecipient(application),
    [application]
  );

  const archiveEntries = useMemo(
    () => (projection ? collectScanArchiveDocumentEntries(projection) : []),
    [projection]
  );

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setArchiveDownloaded(false);
      setArchiveDownloading(false);
      return;
    }

    form.setFieldsValue({
      dispatchedAt: dayjs(),
      recipient: defaultRecipient,
      comment: '',
    });
  }, [defaultRecipient, form, open, applicationId]);

  const handleDownloadArchive = useCallback(async () => {
    if (!projection) {
      message.error('Не удалось загрузить данные комплекта');
      return;
    }

    if (archiveEntries.length === 0) {
      message.warning('Нет документов, готовых к отправке');
      return;
    }

    setArchiveDownloading(true);
    try {
      await downloadScanDocumentsArchive(
        archiveEntries,
        buildScanArchiveDownloadFilename(applicationId)
      );
      setArchiveDownloaded(true);
      message.success('Архив документов скачан');
    } catch (error) {
      const code = error instanceof Error ? error.message : '';
      if (code === 'SCAN_ARCHIVE_EMPTY') {
        message.warning('Нет документов для архива');
      } else {
        message.error('Не удалось сформировать архив документов');
      }
    } finally {
      setArchiveDownloading(false);
    }
  }, [applicationId, archiveEntries, projection]);

  const handleSubmit = useCallback(async () => {
    if (!projection) {
      message.error('Не удалось загрузить данные комплекта');
      return;
    }

    if (!archiveDownloaded) {
      message.info('Для подтверждения отправки необходимо скачать архив документов');
      return;
    }

    let values: SendScansModalFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const validationIssue = validateSendScansSubmit({
      form: {
        eventAt: values.dispatchedAt,
        recipient: values.recipient,
        comment: values.comment,
      },
      projection,
    });

    if (validationIssue) {
      message.error(validationIssue.message);
      return;
    }

    try {
      await registerScanDispatch({
        applicationId,
        body: buildScanDispatchBody({
          recipient: values.recipient,
          comment: values.comment,
          dispatchedAt: values.dispatchedAt.toISOString(),
        }),
      }).unwrap();

      message.success('Отправка сканов зафиксирована');
      await onSuccess?.();
      onClose();
    } catch (error) {
      message.error(mapClosingPackageApiError(error));
    }
  }, [
    applicationId,
    archiveDownloaded,
    form,
    onClose,
    onSuccess,
    projection,
    registerScanDispatch,
  ]);

  return {
    form,
    projection,
    contextView,
    contextLoading,
    submitting: states.registerScanDispatch.isLoading,
    archiveDownloaded,
    archiveDownloading,
    archiveEntryCount: archiveEntries.length,
    handleDownloadArchive,
    handleSubmit,
    canSubmit: archiveDownloaded,
  };
}

export type UseSendScansModalResult = ReturnType<typeof useSendScansModal>;
